import React, { useState, useEffect } from 'react';
import { 
  UploadCloud, FileText, CheckCircle2, XCircle, AlertCircle, 
  Send, Play, Trash2, Eye, Download, RefreshCw
} from 'lucide-react';
import api from '../services/api';

function BulkPdfSender() {
  const [step, setStep] = useState(1); // 1: Upload, 2: Conferência, 3: Envio
  const [batches, setBatches] = useState([]);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [pdfs, setPdfs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [batchName, setBatchName] = useState('');
  const [matching, setMatching] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    try {
      const res = await api.get('/api/bulk-pdfs');
      setBatches(res.data);
    } catch (err) {
      console.error('Erro ao carregar lotes:', err);
    }
  };

  // ============================================
  // UPLOAD DE PDFS
  // ============================================
  const handleUpload = async (e) => {
    e.preventDefault();
    const files = e.target.files.files;
    
    if (!files || files.length === 0) {
      alert('Selecione pelo menos um arquivo PDF');
      return;
    }

    if (files.length > 1000) {
      alert('Máximo de 1000 arquivos por lote');
      return;
    }

    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });

    try {
      const formData = new FormData();
      formData.append('batch_name', batchName || `Lote ${new Date().toLocaleString('pt-BR')}`);
      
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }

      const res = await api.post('/api/bulk-pdfs/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000 // 10 minutos
      });

      setUploadProgress({ done: res.data.processed, total: files.length });
      
      if (res.data.processed > 0) {
        setCurrentBatch(res.data);
        setStep(2);
        loadBatchDetails(res.data.batch_id);
      }

      if (res.data.errors.length > 0) {
        alert(
          `Upload concluído com ${res.data.errors.length} erro(s).\n\n` +
          `Arquivos com erro:\n${res.data.errors.slice(0, 5).map(e => `• ${e.filename}: ${e.error}`).join('\n')}` +
          (res.data.errors.length > 5 ? `\n... e mais ${res.data.errors.length - 5}` : '')
        );
      } else {
        alert(`✅ ${res.data.processed} arquivos enviados com sucesso!`);
      }

      loadBatches();
    } catch (err) {
      console.error('Erro no upload:', err);
      alert('Erro no upload: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  // ============================================
  // CARREGAR DETALHES DO LOTE
  // ============================================
  const loadBatchDetails = async (batchId) => {
    try {
      const res = await api.get(`/api/bulk-pdfs/${batchId}`);
      setCurrentBatch(res.data.batch);
      setPdfs(res.data.pdfs);
    } catch (err) {
      console.error('Erro ao carregar lote:', err);
    }
  };

  // ============================================
  // CONFERÊNCIA
  // ============================================
  const handleMatch = async () => {
    if (!currentBatch) return;
    
    if (!window.confirm(
      `Iniciar conferência automática?\n\n` +
      `O sistema vai tentar encontrar os clientes no banco de contatos\n` +
      `usando o CPF e nome extraídos dos arquivos.`
    )) return;

    setMatching(true);
    try {
      const res = await api.post(`/api/bulk-pdfs/${currentBatch.batch_id}/match`);
      
      alert(
        `✅ Conferência concluída!\n\n` +
        `✓ ${res.data.matched} clientes encontrados\n` +
        `✗ ${res.data.errors} não encontrados`
      );

      loadBatchDetails(currentBatch.batch_id);
      loadBatches();
    } catch (err) {
      console.error('Erro na conferência:', err);
      alert('Erro: ' + (err.response?.data?.error || err.message));
    } finally {
      setMatching(false);
    }
  };

  // ============================================
  // ENVIAR
  // ============================================
  const handleSend = async () => {
    const matchedCount = pdfs.filter(p => p.status === 'matched').length;
    
    if (matchedCount === 0) {
      alert('Nenhum cliente encontrado para enviar');
      return;
    }

    if (!window.confirm(
      `Iniciar envio de ${matchedCount} boletos?\n\n` +
      `⚠️ O envio pode levar alguns minutos.\n` +
      `Os boletos serão enviados via WhatsApp.`
    )) return;

    setSending(true);
    try {
      await api.post(`/api/bulk-pdfs/${currentBatch.batch_id}/send`);
      
      alert(
        `✅ Envio iniciado!\n\n` +
        `O envio está sendo processado em background.\n` +
        `Você pode acompanhar o progresso clicando no lote.`
      );

      setStep(1);
      setCurrentBatch(null);
      setPdfs([]);
      loadBatches();
    } catch (err) {
      console.error('Erro ao enviar:', err);
      alert('Erro: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  // ============================================
  // DELETAR LOTE
  // ============================================
  const handleDelete = async (batchId) => {
    if (!window.confirm('Deletar este lote? Todos os arquivos serão removidos.')) return;
    
    try {
      await api.delete(`/api/bulk-pdfs/${batchId}`);
      loadBatches();
      if (currentBatch?.batch_id === batchId) {
        setCurrentBatch(null);
        setPdfs([]);
        setStep(1);
      }
    } catch (err) {
      alert('Erro ao deletar: ' + (err.response?.data?.error || err.message));
    }
  };

  // Estatísticas
  const stats = {
    total: pdfs.length,
    matched: pdfs.filter(p => p.status === 'matched').length,
    pending: pdfs.filter(p => p.status === 'pending').length,
    sent: pdfs.filter(p => p.status === 'sent').length,
    failed: pdfs.filter(p => p.status === 'failed').length,
    error: pdfs.filter(p => p.status === 'error').length
  };

  const statusColors = {
    pending: 'bg-gray-100 text-gray-700',
    matched: 'bg-blue-100 text-blue-700',
    sending: 'bg-yellow-100 text-yellow-700',
    sent: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    error: 'bg-orange-100 text-orange-700'
  };

  const statusLabels = {
    pending: 'Pendente',
    matched: 'Cliente Encontrado',
    sending: 'Enviando...',
    sent: 'Enviado',
    failed: 'Falhou',
    error: 'Erro'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileText size={28} className="text-purple-600" />
          Envio de Boletos em Massa
        </h1>
        <p className="text-gray-500">
          Faça upload de PDFs e envie automaticamente para os clientes via WhatsApp
        </p>
      </div>

      {/* Indicador de Passo */}
      <div className="flex items-center justify-center gap-2 bg-white p-4 rounded-2xl border border-gray-100">
        {[
          { n: 1, label: 'Upload' },
          { n: 2, label: 'Conferência' },
          { n: 3, label: 'Envio' }
        ].map((s, idx) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${
              step === s.n 
                ? 'bg-purple-600 text-white' 
                : step > s.n 
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-500'
            }`}>
              <span className="font-bold">{s.n}</span>
              <span className="text-sm font-medium">{s.label}</span>
            </div>
            {idx < 2 && <div className="w-8 h-0.5 bg-gray-200"></div>}
          </React.Fragment>
        ))}
      </div>

      {/* ============================================ */}
      {/* STEP 1: UPLOAD */}
      {/* ============================================ */}
      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Formulário de Upload */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <UploadCloud size={20} className="text-purple-600" />
              Upload de Boletos
            </h2>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-blue-900 font-semibold mb-2">
                📋 Formato dos Arquivos:
              </p>
              <p className="text-xs text-blue-800 mb-2">
                Os arquivos devem seguir o padrão:
              </p>
              <code className="block bg-white px-3 py-2 rounded text-xs font-mono text-purple-700 mb-2">
                Nome_Sobrenome_CPF_000.000.000-00.pdf
              </code>
              <p className="text-xs text-blue-800">
                Exemplo: <code className="bg-white px-1 rounded">Danilo_Porto_CPF_230.564.358-62.pdf</code>
              </p>
            </div>

            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Nome do Lote (opcional)
                </label>
                <input
                  type="text"
                  value={batchName}
                  onChange={e => setBatchName(e.target.value)}
                  placeholder="Ex: Boletos Junho 2026"
                  className="w-full p-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Arquivos PDF * (máx. 1000)
                </label>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-purple-400 transition-colors">
                  <UploadCloud className="mx-auto text-gray-400 mb-2" size={40} />
                  <p className="text-sm text-gray-600 mb-2">
                    Selecione até 1000 arquivos PDF
                  </p>
                  <input
                    type="file"
                    name="files"
                    accept=".pdf"
                    multiple
                    required
                    className="block mx-auto text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={uploading}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl font-bold hover:from-purple-700 hover:to-purple-800 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Enviando {uploadProgress.done}/{uploadProgress.total}...
                  </>
                ) : (
                  <>
                    <UploadCloud size={20} />
                    Fazer Upload
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Lista de Lotes Existentes */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <FileText size={20} className="text-blue-600" />
              Lotes Anteriores
            </h2>
            
            {batches.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">
                Nenhum lote encontrado
              </p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {batches.map(batch => (
                  <div
                    key={batch.id}
                    className="p-3 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setCurrentBatch(batch);
                      loadBatchDetails(batch.batch_id);
                      setStep(2);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {batch.name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {batch.total_files} arquivos
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(batch.batch_id);
                        }}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                        {batch.matched_count || 0} match
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded">
                        {batch.sent_count || 0} enviados
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-red-50 text-red-700 rounded">
                        {batch.error_count || 0} erros
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* STEP 2: CONFERÊNCIA */}
      {/* ============================================ */}
      {step === 2 && currentBatch && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold">{currentBatch.name}</h2>
              <p className="text-sm text-gray-500">
                Lote: {currentBatch.batch_id}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm hover:bg-gray-50"
              >
                ← Voltar
              </button>
              <button
                onClick={() => loadBatchDetails(currentBatch.batch_id)}
                className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50"
                title="Atualizar"
              >
                <RefreshCw size={16} />
              </button>
            </div>
          </div>

          {/* Estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            <div className="bg-gray-50 p-3 rounded-xl">
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-blue-50 p-3 rounded-xl">
              <p className="text-xs text-blue-600">Encontrados</p>
              <p className="text-2xl font-bold text-blue-900">{stats.matched}</p>
            </div>
            <div className="bg-yellow-50 p-3 rounded-xl">
              <p className="text-xs text-yellow-600">Pendentes</p>
              <p className="text-2xl font-bold text-yellow-900">{stats.pending}</p>
            </div>
            <div className="bg-green-50 p-3 rounded-xl">
              <p className="text-xs text-green-600">Enviados</p>
              <p className="text-2xl font-bold text-green-900">{stats.sent}</p>
            </div>
            <div className="bg-red-50 p-3 rounded-xl">
              <p className="text-xs text-red-600">Falhas</p>
              <p className="text-2xl font-bold text-red-900">{stats.failed}</p>
            </div>
            <div className="bg-orange-50 p-3 rounded-xl">
              <p className="text-xs text-orange-600">Erros</p>
              <p className="text-2xl font-bold text-orange-900">{stats.error}</p>
            </div>
          </div>

          {/* Ações */}
          <div className="flex gap-3 mb-6">
            {stats.pending > 0 && (
              <button
                onClick={handleMatch}
                disabled={matching}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {matching ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Conferindo...
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} />
                    Conferir ({stats.pending} pendentes)
                  </>
                )}
              </button>
            )}
            {stats.matched > 0 && (
              <button
                onClick={handleSend}
                disabled={sending}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Enviar {stats.matched} boletos
                  </>
                )}
              </button>
            )}
          </div>

          {/* Lista de PDFs */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase sticky top-0">
                  <tr>
                    <th className="px-4 py-3">Arquivo</th>
                    <th className="px-4 py-3">Nome Extraído</th>
                    <th className="px-4 py-3">CPF</th>
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Erro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pdfs.map(pdf => (
                    <tr key={pdf.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs font-mono text-gray-700">
                        {pdf.original_filename}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {pdf.extracted_name || '—'}
                      </td>
                      <td className="px-4 py-2 text-sm font-mono text-gray-700">
                        {pdf.extracted_cpf || '—'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {pdf.matched_contact_name ? (
                          <div>
                            <p className="font-medium">{pdf.matched_contact_name}</p>
                            <p className="text-xs text-gray-500">{pdf.matched_wa_id}</p>
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${statusColors[pdf.status]}`}>
                          {statusLabels[pdf.status]}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-red-600 max-w-xs truncate">
                        {pdf.error_message || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BulkPdfSender;